import { useCallback, useState } from 'react';

/**
 * Минимальный помощник форм: валидация на blur и на submit (docs/06 §5.9),
 * кнопка submit блокируется только на время запроса.
 * Библиотеки форм в стеке нет — 40 строк хватает.
 */

export type Validator<T> = (values: T) => Partial<Record<keyof T, string>>;

export type FormState<T> = {
  readonly values: T;
  readonly errors: Partial<Record<keyof T, string>>;
  readonly isDirty: boolean;
  setValue: <K extends keyof T>(key: K, value: T[K]) => void;
  setValues: (values: T) => void;
  handleBlur: (key: keyof T) => void;
  /** Возвращает `true`, если форма валидна. */
  validate: () => boolean;
  reset: (next?: T) => void;
};

export function useFormState<T extends object>(initial: T, validate: Validator<T>): FormState<T> {
  const [values, setValuesState] = useState<T>(initial);
  const [errors, setErrors] = useState<Partial<Record<keyof T, string>>>({});
  const [isDirty, setDirty] = useState(false);

  const setValue = useCallback(
    <K extends keyof T>(key: K, value: T[K]) => {
      setValuesState((previous) => ({ ...previous, [key]: value }));
      setDirty(true);
      setErrors((previous) => {
        if (!(key in previous)) {
          return previous;
        }
        const next = { ...previous };
        delete next[key];
        return next;
      });
    },
    [],
  );

  const setValues = useCallback((next: T) => {
    setValuesState(next);
    setDirty(true);
  }, []);

  const handleBlur = useCallback(
    (key: keyof T) => {
      setErrors((previous) => ({ ...previous, [key]: validate(values)[key] }));
    },
    [validate, values],
  );

  const runValidation = useCallback((): boolean => {
    const nextErrors = validate(values);
    setErrors(nextErrors);
    return Object.values(nextErrors).every((message) => message === undefined);
  }, [validate, values]);

  const reset = useCallback(
    (next?: T) => {
      setValuesState(next ?? initial);
      setErrors({});
      setDirty(false);
    },
    [initial],
  );

  return { values, errors, isDirty, setValue, setValues, handleBlur, validate: runValidation, reset };
}
